import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { DOMAIN_COLORS, TYPE_COLORS } from '@/lib/constants';
import { Archive, Copy, Pencil, X, Send } from 'lucide-react';
import { useConfirmation } from '@/components/shared/ZCard';
import OverflowMenu from '@/components/shared/OverflowMenu';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import ZTabs from '@/components/shared/ZTabs';
import ZFilterPills from '@/components/shared/ZFilterPills';
import FilterToggle from '@/components/shared/FilterToggle';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import Chronicle from '@/pages/Chronicle';
import Library from '@/pages/Library';

const DOMAINS = ['soul', 'matrix', 'both'];
const DOMAIN_CYCLE = ['soul', 'matrix', 'both'];
const TYPES = ['realization', 'reflection', 'decision', 'pattern', 'principle'];


function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function SoulSync() {
  const navigate = useNavigate();
  const feedback = useConfirmation();
  const [activeTab, setActiveTab] = useState('entries'); // 'entries' | 'chronicle' | 'pulse' | 'library'
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, soul: 0, matrix: 0, both: 0, thread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [domainFilter, setDomainFilter] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [pulses, setPulses] = useState([]);
  const [pulsesLoading, setPulsesLoading] = useState(false);
  const [expandedPulseDate, setExpandedPulseDate] = useState(null);

  // Simple input state
  const [inputOpen, setInputOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);

  // Track visual viewport so input stays above keyboard on mobile
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || !inputOpen) return;
    const update = () => {
      if (inputContainerRef.current) {
        const keyboardOffset = window.innerHeight - vv.height - vv.offsetTop;
        inputContainerRef.current.style.bottom = `${Math.max(0, keyboardOffset)}px`;
      }
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [inputOpen]);

  // Inline edit state
  const [editingTitle, setEditingTitle] = useState(null); // entry id
  const [editTitleVal, setEditTitleVal] = useState('');
  const [editingBody, setEditingBody] = useState(null);
  const [editBodyVal, setEditBodyVal] = useState('');
  const [threadInput, setThreadInput] = useState('');

  const hasSidebar = window.innerWidth >= 768;

  useEffect(() => {
    loadEntries();
    loadStats();
  }, [domainFilter, typeFilter]);

  useEffect(() => {
    if (activeTab === 'pulse' && pulses.length === 0) loadPulses();
  }, [activeTab]);

  const loadPulses = async () => {
    setPulsesLoading(true);
    try {
      const { data } = await api.get('/zephyr/pulses');
      setPulses(data);
    } catch { /* silent */ } finally { setPulsesLoading(false); }
  };

  // FAB listener
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.app === 'soulsync') openInput();
    };
    window.addEventListener('fab-new-entry', handler);
    return () => window.removeEventListener('fab-new-entry', handler);
  }, []);

  const loadEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (domainFilter) params.set('domain', domainFilter);
      if (typeFilter) params.set('type', typeFilter);
      params.set('limit', '100');
      const { data } = await api.get(`/soulsync/entries?${params}`);
      setEntries(data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/soulsync/stats');
      setStats(data);
    } catch { /* silent */ }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setEditingTitle(null);
    setEditingBody(null);
    setThreadInput('');
  };

  const openInput = () => {
    setInputOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const closeInput = () => {
    setInputOpen(false);
    setInputText('');
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data: classified } = await api.post('/soulsync/classify', { text });
      await api.post('/soulsync/entries', {
        title: classified.title,
        body: classified.body,
        domain: classified.domain,
        type: classified.type,
        threads: classified.threads || [],
        origin: 'Manual paste',
      });
      feedback.confirm();
      closeInput();
      loadEntries();
      loadStats();
    } catch { feedback.fail(); }
    finally { setSaving(false); }
  };

  // Inline update helper — optimistic
  const updateEntry = async (id, updates) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e));
    try {
      await api.put(`/soulsync/entries/${id}`, updates);
    } catch {
      feedback.fail();
      loadEntries();
    }
  };

  const cycleDomain = (entry) => {
    const idx = DOMAIN_CYCLE.indexOf(entry.domain);
    const next = DOMAIN_CYCLE[(idx + 1) % DOMAIN_CYCLE.length];
    updateEntry(entry.id, { domain: next });
  };

  const cycleType = (entry) => {
    const idx = TYPES.indexOf(entry.type);
    const next = TYPES[(idx + 1) % TYPES.length];
    updateEntry(entry.id, { type: next });
  };

  const saveTitle = (entry) => {
    const val = editTitleVal.trim();
    if (val && val !== entry.title) updateEntry(entry.id, { title: val });
    setEditingTitle(null);
  };

  const saveBody = (entry) => {
    const val = editBodyVal.trim();
    if (val && val !== entry.body) updateEntry(entry.id, { body: val });
    setEditingBody(null);
  };

  const addThread = (entry) => {
    const tag = threadInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag) return;
    const threads = [...(entry.threads || [])];
    if (!threads.includes(tag)) {
      threads.push(tag);
      updateEntry(entry.id, { threads });
    }
    setThreadInput('');
  };

  const removeThread = (entry, tag) => {
    const threads = (entry.threads || []).filter(t => t !== tag);
    updateEntry(entry.id, { threads });
  };

  const [archiveConfirm, setArchiveConfirm] = useState({ open: false, id: null, name: '' });

  const archiveEntry = async (id) => {
    const prev = entries;
    setEntries(entries.filter(e => e.id !== id));
    try {
      await api.put(`/soulsync/entries/${id}`, { status: 'archived' });
      feedback.confirm();
      loadStats();
    } catch {
      setEntries(prev);
      feedback.fail();
    }
  };

  const copyEntry = (entry) => {
    navigator.clipboard.writeText(`${entry.title}\n\n${entry.body}`);
    feedback.confirm();
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto pb-40">
      {/* Squircle defs now provided globally by Shell via SquircleDefs */}

      {/* Header */}
      <div className="flex items-center mb-1" style={{ gap: 2 }}>
        <img
          src="/logo-soulsync.png"
          alt="SoulSync"
          style={{ width: 36, height: 36, clipPath: 'url(#squircle)', flexShrink: 0 }}
        />
        <span style={{
          fontFamily: 'system-ui, sans-serif',
          fontSize: 28, fontWeight: 300,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          color: '#f0f0f5', lineHeight: 1
        }}>OULSYNC</span>
      </div>
      <p className="text-[#b0b4c0] text-xs tracking-[0.2em] uppercase font-mono" style={{ paddingLeft: 42 }}>
        Architecting The Self
      </p>

      {/* Tab bar */}
      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <ZTabs
          tabs={[
            { id: 'entries', label: 'Codex' },
            { id: 'pulse', label: 'Pulse' },
            { id: 'chronicle', label: 'Chronicle' },
            { id: 'library', label: 'Library' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
          accent="#4B5FD4"
        />
        {activeTab === 'entries' && (
          <FilterToggle
            isExpanded={filterOpen}
            onToggle={() => setFilterOpen(o => !o)}
            activeCount={(domainFilter ? 1 : 0) + (typeFilter ? 1 : 0)}
            accent="#4B5FD4"
          />
        )}
      </div>

      {/* Chronicle tab */}
      {activeTab === 'chronicle' && (
        <div style={{ marginTop: 20 }}>
          <Chronicle />
        </div>
      )}

      {/* Library tab */}
      {activeTab === 'library' && (
        <div className="-mx-6 md:-mx-10" style={{ marginTop: 8 }}>
          <Library embedded />
        </div>
      )}

      {/* Filter pills — entries only, collapsible */}
      {activeTab === 'entries' && filterOpen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <ZFilterPills
            options={DOMAINS.map(d => ({ id: d, label: d }))}
            selected={domainFilter}
            onChange={setDomainFilter}
            accent="#4B5FD4"
          />
          <span style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
          <ZFilterPills
            options={TYPES.map(t => ({ id: t, label: t }))}
            selected={typeFilter}
            onChange={(v) => setTypeFilter(v)}
            accent="#4B5FD4"
          />
        </div>
      )}

      {/* Pulse archive */}
      {activeTab === 'pulse' && (
        <div style={{ marginTop: 16 }}>
          <ZLoadingBar accent="#6B35B8" loading={pulsesLoading} />
          {!pulsesLoading && pulses.length === 0 && <ZEmptyState mark="S" accent="#6B35B8" message="No pulses yet — they generate daily." />}
          {pulses.map(pulse => {
            const expanded = expandedPulseDate === pulse.date;
            const snap = pulse.corpus_snapshot || {};
            return (
              <div
                key={pulse.date}
                onClick={() => setExpandedPulseDate(expanded ? null : pulse.date)}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '0.5px solid rgba(255,255,255,0.06)',
                  borderLeft: `2px solid rgba(107,53,184,0.6)`,
                  borderRadius: 10, padding: 14, marginBottom: 8, cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#454860', letterSpacing: '0.08em', flexShrink: 0, paddingTop: 2 }}>
                    {pulse.date}
                  </span>
                  <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 13, color: '#f0f0f5', lineHeight: 1.5, flex: 1 }}>
                    {pulse.sentence}
                  </p>
                </div>
                {expanded && (
                  <div onClick={e => e.stopPropagation()}>
                    {pulse.elaboration && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                        {pulse.elaboration.split('\n\n').map((para, i) => (
                          <p key={i} style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 13, color: '#b0b4c0', lineHeight: 1.8, marginBottom: i === 0 ? 10 : 0 }}>
                            {para}
                          </p>
                        ))}
                      </div>
                    )}
                    {Object.keys(snap).length > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {snap.soulsync_entry_count != null && <span style={snapPillStyle}>{snap.soulsync_entry_count} SS entries</span>}
                        {snap.soulsync_entries_this_week != null && <span style={snapPillStyle}>{snap.soulsync_entries_this_week} this week</span>}
                        {snap.open_uncode_count != null && <span style={snapPillStyle}>{snap.open_uncode_count} open tasks</span>}
                        {snap.active_trip && <span style={{ ...snapPillStyle, color: '#10B981' }}>{snap.active_trip}</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Entry stream */}
      {activeTab === 'entries' && <div style={{ marginTop: 16, position: 'relative' }}>
        <OverflowMenu
          placement="tab"
          items={[
            { icon: <Archive size={16} strokeWidth={1.5} />, label: 'View archive', onClick: () => navigate('/soulsync/codex/archive') },
          ]}
        />
        {/* Stats — first row of content area per Section 10.x */}
        <div className="flex gap-2 mb-3">
          <div style={statPillStyle}>{stats.total} entries</div>
          <div style={statPillStyle}>{stats.thread_count} threads</div>
          <div style={{ ...statPillStyle, display: 'flex', gap: 6 }}>
            <span style={{ color: '#AFA9EC' }}>{stats.soul}</span>
            <span style={{ color: '#454860' }}>·</span>
            <span style={{ color: '#107270' }}>{stats.matrix}</span>
            <span style={{ color: '#454860' }}>·</span>
            <span style={{ color: '#E8C547' }}>{stats.both}</span>
          </div>
        </div>
        <ZLoadingBar accent="#6B35B8" loading={loading} />
        {!loading && entries.length === 0 && <ZEmptyState mark="S" accent="#6B35B8" message="No entries yet" />}
        {entries.map((entry) => {
          const expanded = expandedId === entry.id;
          const domainColor = DOMAIN_COLORS[entry.domain] || '#b0b4c0';
          return (
            <div
              key={entry.id}
              style={{
                position: 'relative',
                background: 'rgba(255,255,255,0.03)',
                border: '0.5px solid rgba(255,255,255,0.06)',
                borderLeft: `2px solid ${domainColor}`,
                borderRadius: 10,
                padding: 14,
                cursor: 'pointer',
                marginBottom: 8,
              }}
              onClick={() => toggleExpand(entry.id)}
            >
              <OverflowMenu
                items={[
                  { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => { setExpandedId(entry.id); setEditingTitle(entry.id); setEditTitleVal(entry.title); } },
                  { icon: <Archive size={16} strokeWidth={1.5} />, label: 'Archive', onClick: () => setArchiveConfirm({ open: true, id: entry.id, name: entry.title || 'this entry' }) },
                  { icon: <Copy size={16} strokeWidth={1.5} />, label: 'Copy', onClick: () => copyEntry(entry) },
                ]}
              />

              {/* Collapsed */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{
                  fontSize: 13, fontWeight: 500, color: '#f0f0f5',
                  fontFamily: 'Georgia, serif', lineHeight: 1.4,
                  flex: 1, overflow: 'hidden',
                  display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical'
                }}>
                  {entry.title}
                </div>
                <div style={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 3,
                  background: DOMAIN_COLORS[entry.domain]
                }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <span style={{
                  background: `${TYPE_COLORS[entry.type]}18`, color: TYPE_COLORS[entry.type],
                  fontSize: 9, padding: '2px 7px', borderRadius: 4, fontWeight: 500,
                  letterSpacing: '0.04em', textTransform: 'uppercase'
                }}>
                  {entry.type}
                </span>
                {(entry.threads || []).slice(0, 2).map(t => (
                  <span key={t} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(255,255,255,0.04)', color: '#7A7A9A' }}>
                    #{t}
                  </span>
                ))}
                <span style={{ fontSize: 10, color: '#b0b4c0', fontFamily: 'monospace', marginLeft: 'auto' }}>
                  {formatDate(entry.created_at)}
                </span>
              </div>

              {/* Expanded */}
              {expanded && (
                <div onClick={e => e.stopPropagation()}>
                  {/* Editable title */}
                  {editingTitle === entry.id ? (
                    <input
                      autoFocus
                      value={editTitleVal}
                      onChange={e => setEditTitleVal(e.target.value)}
                      onBlur={() => saveTitle(entry)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingTitle(null); }}
                      style={{
                        width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)',
                        outline: 'none', color: '#f0f0f5', fontSize: 13, fontWeight: 500,
                        fontFamily: 'Georgia, serif', marginTop: 10, paddingBottom: 4,
                      }}
                    />
                  ) : (
                    <p
                      onClick={() => { setEditingTitle(entry.id); setEditTitleVal(entry.title); }}
                      style={{
                        fontFamily: 'Georgia, serif', fontWeight: 500, color: '#f0f0f5',
                        fontSize: 13, marginTop: 10, cursor: 'text',
                      }}
                    >
                      {entry.title}
                    </p>
                  )}

                  {/* Editable body */}
                  {editingBody === entry.id ? (
                    <textarea
                      autoFocus
                      value={editBodyVal}
                      onChange={e => {
                        setEditBodyVal(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      onBlur={() => saveBody(entry)}
                      style={{
                        width: '100%', background: 'transparent', border: '0.5px solid rgba(255,255,255,0.06)',
                        borderRadius: 6, outline: 'none', color: '#b0b4c0', fontSize: 13,
                        fontFamily: 'Georgia, serif', fontStyle: 'italic', lineHeight: 1.8,
                        padding: '8px 10px', resize: 'none', marginTop: 8, minHeight: 60,
                      }}
                    />
                  ) : (
                    <p
                      onClick={() => { setEditingBody(entry.id); setEditBodyVal(entry.body); }}
                      style={{
                        fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#b0b4c0',
                        lineHeight: 1.8, fontSize: 13, marginTop: 6, whiteSpace: 'pre-wrap', cursor: 'text',
                      }}
                    >
                      {entry.body}
                    </p>
                  )}

                  {/* Domain + Type — tap to cycle */}
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
                    <button
                      onClick={() => cycleDomain(entry)}
                      style={{
                        background: `${DOMAIN_COLORS[entry.domain]}22`,
                        color: DOMAIN_COLORS[entry.domain],
                        border: `0.5px solid ${DOMAIN_COLORS[entry.domain]}55`,
                        borderRadius: 20, padding: '3px 10px', fontSize: 10,
                        fontFamily: 'monospace', textTransform: 'capitalize', cursor: 'pointer',
                      }}
                    >
                      {entry.domain}
                    </button>
                    <button
                      onClick={() => cycleType(entry)}
                      style={{
                        background: `${TYPE_COLORS[entry.type]}18`,
                        color: TYPE_COLORS[entry.type],
                        border: `0.5px solid ${TYPE_COLORS[entry.type]}44`,
                        borderRadius: 20, padding: '3px 10px', fontSize: 10,
                        fontFamily: 'monospace', textTransform: 'capitalize', cursor: 'pointer',
                      }}
                    >
                      {entry.type}
                    </button>
                  </div>

                  {/* Threads — removable + add */}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                    {(entry.threads || []).map(t => (
                      <span key={t} style={{
                        background: 'rgba(75,95,212,0.08)', color: '#4B5FD4',
                        border: '0.5px solid rgba(75,95,212,0.15)',
                        fontSize: 10, padding: '2px 8px', borderRadius: 4,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        #{t}
                        <button onClick={() => removeThread(entry, t)} style={{ color: '#4B5FD4', lineHeight: 1 }}>
                          <X size={8} />
                        </button>
                      </span>
                    ))}
                    <input
                      value={threadInput}
                      onChange={e => {
                        const val = e.target.value;
                        if (val.endsWith(',') || val.endsWith('\n')) {
                          const tag = val.replace(/[,\n]/g, '').trim().toLowerCase().replace(/\s+/g, '-');
                          if (tag) {
                            const threads = [...(entry.threads || [])];
                            if (!threads.includes(tag)) {
                              threads.push(tag);
                              updateEntry(entry.id, { threads });
                            }
                          }
                          setThreadInput('');
                        } else {
                          setThreadInput(val);
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); addThread(entry); }
                      }}
                      placeholder="+ thread"
                      style={{
                        background: 'transparent', border: 'none', outline: 'none',
                        color: '#7A7A9A', fontSize: 10, fontFamily: 'monospace',
                        width: 70, padding: '2px 0',
                      }}
                    />
                  </div>

                  {entry.origin && (
                    <p style={{ fontSize: 10, color: '#454860', fontFamily: 'monospace', marginTop: 8 }}>
                      {entry.origin}
                    </p>
                  )}

                </div>
              )}
              {/* Bottom-left accent line */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, width: '50%', height: '2px', background: `linear-gradient(to right, ${domainColor}, transparent)`, borderRadius: '0 999px 999px 0', pointerEvents: 'none' }} />
            </div>
          );
        })}
      </div>}

      {/* Trigger bar — fixed bottom, 70% width like Uncode — hidden on Chronicle/Library tabs */}
      {!inputOpen && activeTab !== 'chronicle' && activeTab !== 'library' && (
        <div className={`fixed bottom-[72px] md:bottom-8 left-0 ${hasSidebar ? 'md:left-64' : ''} right-0 z-30 pointer-events-none flex justify-center`}>
          <div className="w-[70%] max-w-xl">
            <button
              onClick={openInput}
              className="pointer-events-auto w-full flex items-center gap-3 border-2 border-[#4B5FD4]/80 rounded-xl px-4 py-3 text-left shadow-xl hover:border-[#4B5FD4] active:scale-[0.99] transition-all backdrop-blur-xl"
              style={{ background: 'rgba(10, 10, 15, 0.80)' }}
            >
              <span className="flex-1 text-sm text-white font-medium">Paste into the cosmos…</span>
              <Send size={18} className="text-white/70 shrink-0" />
            </button>
          </div>
        </div>
      )}

      {/* Open input — fixed bottom, 70% width */}
      {inputOpen && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={closeInput} />
          <div
            ref={inputContainerRef}
            className={`fixed bottom-0 left-0 ${hasSidebar ? 'md:left-64' : ''} right-0 z-[60] pb-2`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-[70%] max-w-xl mx-auto">
              <div className="w-full bg-white/[0.05] border-2 border-[#4B5FD4]/80 rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                  <span className="text-[10px] font-mono text-white tracking-widest uppercase flex-1">Add to the cosmos</span>
                  <button onClick={closeInput} className="p-1 text-white/30 hover:text-white/70 transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <div className="px-4 pb-3">
                  <textarea
                    ref={inputRef}
                    autoFocus
                    value={inputText}
                    rows={1}
                    onChange={(e) => {
                      setInputText(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Paste a thought, realization, pattern…"
                    className="w-full bg-transparent text-sm text-[#f0f0f5] font-light leading-relaxed outline-none resize-none placeholder:text-[#b0b4c0]/40"
                    style={{ maxHeight: '40vh', fontSize: '16px' }}
                  />
                  <div className="flex items-center justify-end mt-2">
                    <button
                      onClick={handleSend}
                      disabled={saving || !inputText.trim()}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#4B5FD4] text-white text-xs font-mono tracking-wider uppercase transition-all hover:bg-[#5A6BE0] disabled:opacity-40"
                    >
                      {saving ? 'Saving…' : 'Send'}
                      <Send size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <DeleteConfirmDialog
        open={archiveConfirm.open}
        onClose={() => setArchiveConfirm({ open: false, id: null, name: '' })}
        onConfirm={() => archiveEntry(archiveConfirm.id)}
        itemName={archiveConfirm.name}
        itemType="entry"
      />
    </div>
  );
}

const snapPillStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '0.5px solid rgba(255,255,255,0.06)',
  borderRadius: 4,
  padding: '2px 8px',
  fontSize: 9,
  fontFamily: 'monospace',
  color: '#454860',
  letterSpacing: '0.06em',
};

const statPillStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '0.5px solid rgba(255,255,255,0.06)',
  borderRadius: 20,
  padding: '4px 12px',
  fontSize: 11,
  color: '#b0b4c0',
  fontFamily: 'monospace',
};
